import { Suspense, lazy, useState, useEffect } from 'react';
import { lazyWithPreload } from '@shared/utils/lazyWithPreload';
import { runAfterFirstInteraction } from '@shared/utils/runAfterFirstInteraction';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import LoginPage from '../../epics/authentication/pages/login/loginPage';
import { ForgotPassword } from '../../epics/authentication/pages/lazyAuthRoutes';
import { PublicRoute } from './PublicRoute';
import { ProtectedRoute } from './ProtectedRoute';
import { NewPasswordRequiredRoute } from './NewPasswordRequiredRoute';
import MinimalLayout from '../../shared/layout/noInternetLayout/MinimalLayout';
import Loader from './Loader';
import Snackbar from '@/shared/components/Snackbar/Snackbar';
import { useUserRole } from '@/shared/hooks/useUserRole';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';
import RouteFlashSnackbar from '@/shared/components/RouteFlashSnackbar/RouteFlashSnackbar';
import { ChunkErrorBoundary } from '@/shared/components/ChunkErrorBoundary';
import type { FillTaskFormNavState } from '@/epics/taskFormManagement/pages/fillForm/FillTaskForm';
import LayOut from '../../shared/layout/mainLayout';
import { TemperoryFile } from './lazyPages';

// Every page behind login is code-split so the login page only downloads what
// it needs; each page's chunk loads the first time it is opened.
const ErrorPage404 = lazyWithPreload(() => import('./errorPage404'));
const ErrorPage403 = lazyWithPreload(() => import('./errorPage403'));

const ProfilePage = lazyWithPreload(
  () => import('../../epics/userProfile/pages/profile')
);
const EditProfile = lazyWithPreload(
  () => import('../../epics/userProfile/pages/editProfile/EditProfile')
);

const AdminList = lazyWithPreload(
  () => import('../../epics/adminManagment/pages/adminList/AdminList')
);
const AddUser = lazyWithPreload(
  () => import('../../epics/adminManagment/pages/addUsers/AddUser')
);
const ViewAdminPage = lazyWithPreload(
  () => import('@/epics/adminManagment/pages/ViewAdmin/viewAdmin')
);
const EditAdmin = lazyWithPreload(
  () => import('../../epics/adminManagment/pages/editAdmin/EditAdmin')
);

const TechnicianList = lazyWithPreload(
  () => import('../../epics/technicianManagement/pages/technicianList')
);
const AddTechnician = lazyWithPreload(
  () =>
    import('../../epics/technicianManagement/pages/addTechnician/AddTechnician')
);
const EditTechnician = lazyWithPreload(
  () =>
    import(
      '../../epics/technicianManagement/pages/editTechnician/editTechnician'
    )
);
const ViewTechnicianPage = lazyWithPreload(
  () =>
    import('@/epics/technicianManagement/pages/ViewTechnician/viewTechnician')
);

const ClientList = lazyWithPreload(
  () => import('@/epics/clientManagement/pages/clientList/ClientList')
);
const AddClient = lazyWithPreload(
  () => import('@/epics/clientManagement/pages/addClient/AddClient')
);
const EditClient = lazyWithPreload(
  () => import('@/epics/clientManagement/pages/editClient/EditClient')
);
const ViewClientPage = lazyWithPreload(
  () => import('@/epics/clientManagement/pages/viewClient/ViewClient')
);
const PermissionsPage = lazyWithPreload(
  () => import('@/epics/clientManagement/pages/permissions/PermissionsPage')
);

const SubscriptionList = lazyWithPreload(
  () =>
    import(
      '@/epics/subscriptionManagment/pages/subscriptionList/SubscriptionList'
    )
);
const CreateSubscription = lazyWithPreload(
  () =>
    import(
      '@/epics/subscriptionManagment/pages/createSubscription/CreateSubscription'
    )
);
const ViewSubscriptionPlanPage = lazyWithPreload(
  () =>
    import(
      '@/epics/subscriptionManagment/pages/viewSubscriptionPlan/viewSubscriptionPlan'
    )
);
const EditSubscription = lazyWithPreload(
  () =>
    import(
      '@/epics/subscriptionManagment/pages/editSubscription/EditSubscription'
    )
);

const OfferList = lazyWithPreload(
  () => import('@/epics/offerManagment/pages/offerList/OfferList')
);
const CreateOffer = lazyWithPreload(
  () => import('@/epics/offerManagment/pages/createOffer/CreateOffer')
);
const ViewOffer = lazyWithPreload(
  () => import('@/epics/offerManagment/pages/viewOffer/ViewOffer')
);
const EditOffer = lazyWithPreload(
  () => import('@/epics/offerManagment/pages/editOffer/EditOffer')
);

const FacilityListPage = lazyWithPreload(
  () => import('@/epics/facilityManagement/pages/facilityList/FacilityListPage')
);
const AddFacilityPage = lazyWithPreload(
  () => import('@/epics/facilityManagement/pages/addFacility/AddFacilityPage')
);
const ViewFacilityPage = lazyWithPreload(
  () => import('@/epics/facilityManagement/pages/viewFacility/ViewFacilityPage')
);
const EditFacilityPage = lazyWithPreload(
  () => import('@/epics/facilityManagement/pages/editFacility/EditFacilityPage')
);

const ServiceTypeList = lazyWithPreload(
  () =>
    import('@/epics/serviceManagement/pages/serviceTypeList/ServiceTypeList')
);
const CreateServiceType = lazyWithPreload(
  () =>
    import(
      '@/epics/serviceManagement/pages/createServiceType/CreateServiceType'
    )
);
const ViewServiceType = lazyWithPreload(
  () =>
    import('@/epics/serviceManagement/pages/viewServiceType/ViewServiceType')
);
const EditServiceType = lazyWithPreload(
  () =>
    import('@/epics/serviceManagement/pages/editServiceType/EditServiceType')
);
const AvailableServiceList = lazyWithPreload(
  () =>
    import(
      '@/epics/serviceManagement/pages/availableServiceList/AvailableServiceList'
    )
);
const CreateServiceRequest = lazyWithPreload(
  () =>
    import(
      '@/epics/serviceManagement/pages/createServiceRequest/CreateServiceRequest'
    )
);
const ViewServiceRequest = lazyWithPreload(
  () =>
    import(
      '@/epics/serviceManagement/pages/viewServiceRequest/ViewServiceRequest'
    )
);

const ContactRequestList = lazyWithPreload(() =>
  import('@/epics/contactManagement/pages/contactRequestList').then((mod) => ({
    default: mod.ContactRequestList,
  }))
);
const CreateContactRequest = lazyWithPreload(
  () =>
    import(
      '@/epics/contactManagement/pages/createContactRequest/CreateContactRequest'
    )
);
const ViewContactRequest = lazyWithPreload(
  () =>
    import(
      '@/epics/contactManagement/pages/viewContactRequest/ViewContactRequest'
    )
);

const FormList = lazyWithPreload(
  () => import('@/epics/taskFormManagement/pages/formList/FormList')
);
const CreateForm = lazyWithPreload(
  () => import('@/epics/taskFormManagement/pages/createForm/CreateForm')
);
const ViewForm = lazyWithPreload(
  () => import('@/epics/taskFormManagement/pages/viewForm/ViewForm')
);
const EditForm = lazyWithPreload(
  () => import('@/epics/taskFormManagement/pages/editForm/EditForm')
);
const FillTaskForm = lazyWithPreload(
  () => import('@/epics/taskFormManagement/pages/fillForm/FillTaskForm')
);
const SubmittedFormList = lazyWithPreload(
  () =>
    import(
      '@/epics/taskFormManagement/pages/submittedFormList/SubmittedFormList'
    )
);
const ViewSubmittedForm = lazyWithPreload(
  () =>
    import(
      '@/epics/taskFormManagement/pages/viewSubmittedForm/ViewSubmittedForm'
    )
);

const CertificateList = lazyWithPreload(
  () => import('@/epics/certificateManagement/pages/certificateList')
);
const ViewCertificateTemplate = lazyWithPreload(
  () => import('@/epics/certificateManagement/pages/viewCertificateTemplate')
);
const CreateCertificateTemplate = lazyWithPreload(
  () =>
    import(
      '@/epics/certificateManagement/pages/createCertificateTemplate/CreateCertificateTemplate'
    )
);
const EditCertificateTemplate = lazyWithPreload(
  () => import('@/epics/certificateManagement/pages/editCertificateTemplate')
);

const QmsCategoryList = lazyWithPreload(
  () => import('@/epics/qmsManagement/pages/categoryList/QmsCategoryList')
);
const CreateQmsCategory = lazyWithPreload(
  () => import('@/epics/qmsManagement/pages/createCategory/CreateQmsCategory')
);
const ViewQmsCategory = lazyWithPreload(
  () => import('@/epics/qmsManagement/pages/viewCategory/ViewQmsCategory')
);
const EditQmsCategory = lazyWithPreload(
  () => import('@/epics/qmsManagement/pages/editCategory/EditQmsCategory')
);

const TechnicianSchedulePage = lazyWithPreload(
  () =>
    import(
      '@/epics/technicianManagement/pages/technicianSchedule/TechnicianSchedulePage'
    )
);

const TechnicianSelfSchedulePage = lazyWithPreload(
  () =>
    import(
      '@/epics/technicianManagement/pages/technicianSelfSchedule/TechnicianSelfSchedulePage'
    )
);

const MyCertificates = lazyWithPreload(
  () =>
    import('@/epics/certificateManagement/pages/myCertificates/MyCertificates')
);

const ViewCertificate = lazyWithPreload(
  () =>
    import('@/epics/certificateManagement/pages/myCertificates/ViewCertificate')
);

const MyBilling = lazyWithPreload(
  () => import('@/epics/billingManagement/pages/myBilling/MyBilling')
);

// Lazy load authentication pages - importing from shared module ensures single chunk.
// ForgotPassword is preloadable (login warms it) so lives in lazyAuthRoutes.
const ResetPassword = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.ResetPassword,
  }))
);
const LinkExpired = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.LinkExpired,
  }))
);
const NewPasswordRequired = lazy(() =>
  import('../../epics/authentication/pages/authRoutes').then((mod) => ({
    default: mod.NewPasswordRequired,
  }))
);

function ViewAdminPageWrapper() {
  const { userId } = useParams<{ userId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!userId) {
    return <ErrorPage404 />;
  }

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <ViewAdminPage userId={userId} />;
}

function EditTechnicianWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <EditTechnician />;
}

function ViewTechnicianPageWrapper() {
  const { technicianId } = useParams<{ technicianId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!technicianId) {
    return <ErrorPage404 />;
  }

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <ViewTechnicianPage technicianId={technicianId} />;
}

function ViewClientPageWrapper() {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) {
    return <ErrorPage404 />;
  }
  return <ViewClientPage clientId={clientId} />;
}

function ViewFacilityPageWrapper() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const location = useLocation();
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage as string);
      setShowSuccessSnackbar(true);
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  if (!facilityId) {
    return <ErrorPage404 />;
  }

  const isForbidden = !roleLoading && role !== null && role !== 'CLIENT';

  if (isForbidden) {
    return <Navigate to="/not-found" replace />;
  }

  return (
    <>
      {roleLoading ? null : <ViewFacilityPage facilityId={facilityId} />}
      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
}

function EditFacilityPageWrapper() {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!facilityId) {
    return <ErrorPage404 />;
  }

  const isForbidden = !roleLoading && role !== null && role !== 'CLIENT';

  if (isForbidden) {
    return <Navigate to="/not-found" replace />;
  }

  return roleLoading ? null : <EditFacilityPage facilityId={facilityId} />;
}

function AddFacilityPageWrapper() {
  const { isClient, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && !isClient) {
    return <Navigate to="/not-found" replace />;
  }

  return roleLoading ? null : <AddFacilityPage />;
}

function EditClientRedirect() {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) {
    return <ErrorPage404 />;
  }
  return <Navigate to={`/Klienci/Edycja-klienta/${clientId}/krok-1`} replace />;
}

function ContactRequestListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <ContactRequestList />;
}

function AvailableServiceListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <AvailableServiceList />;
}

function MyBillingWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <MyBilling />
    </Suspense>
  );
}

function CreateServiceRequestWrapper() {
  const { serviceTypeName } = useParams<{ serviceTypeName: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (
    !privilegesLoading &&
    role === 'CLIENT' &&
    !privileges.canCreateServiceRequest
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading || !serviceTypeName) {
    return null;
  }

  return <CreateServiceRequest />;
}

function ViewServiceRequestWrapper() {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['CLIENT', 'ADMIN', 'SUPER_ADMIN', 'TECHNICIAN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!serviceRequestId) {
    return <ErrorPage404 />;
  }

  return <ViewServiceRequest serviceRequestId={serviceRequestId} />;
}

function FillTaskFormWrapper() {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const location = useLocation();
  const navState = (location.state as FillTaskFormNavState | null) ?? undefined;
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (role !== null && !['ADMIN', 'SUPER_ADMIN', 'TECHNICIAN'].includes(role)) {
    return <ErrorPage403 />;
  }

  if (role === 'TECHNICIAN' && !privileges.canFillTaskForm) {
    return <ErrorPage403 />;
  }

  if (!serviceRequestId) {
    return <ErrorPage404 />;
  }

  return (
    <FillTaskForm
      serviceRequestId={serviceRequestId}
      {...(navState?.formId ? { formId: navState.formId } : {})}
      {...(navState ? { navState } : {})}
    />
  );
}

/**
 * Detail view for a task assigned to the calling admin (RR-914), reached from
 * the "Moje zadania" tab. Restricted to ADMIN / SUPER_ADMIN and reads the
 * self-scoped `GET /service-requests/my-tasks/{id}` endpoint (404 when the task
 * is not assigned to the caller). Renders the same {@link ViewServiceRequest}
 * page — tabs and action buttons are identical to the standard detail view.
 */
function MyAssignedTaskViewWrapper() {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!serviceRequestId) {
    return <ErrorPage404 />;
  }

  return (
    <ViewServiceRequest serviceRequestId={serviceRequestId} source="my-tasks" />
  );
}

function ViewContactRequestWrapper() {
  const { contactRequestId } = useParams<{ contactRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!contactRequestId) {
    return <ErrorPage404 />;
  }

  return <ViewContactRequest contactRequestId={contactRequestId} />;
}

/**
 * Detail view for a contact request assigned to the calling admin (RR-1096),
 * reached from the "My Contact Requests" tab. Restricted to ADMIN / SUPER_ADMIN
 * and reads the self-scoped `GET /contact-requests/my-tasks/{id}` endpoint (404
 * when the task is not assigned to the caller). Renders {@link ViewContactRequest}
 * with `source="my-tasks"`, which switches to the summary / assignee / response
 * / history tab set.
 */
function ViewMyAssignedContactRequestWrapper() {
  const { contactRequestId } = useParams<{ contactRequestId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!contactRequestId) {
    return <ErrorPage404 />;
  }

  return (
    <ViewContactRequest contactRequestId={contactRequestId} source="my-tasks" />
  );
}

function ViewFormWrapper() {
  const { formId } = useParams<{ formId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!formId) {
    return <ErrorPage404 />;
  }

  return <ViewForm id={formId} />;
}

function FormListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <FormList />;
}

function CreateFormWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <CreateForm />;
}

function EditFormWrapper() {
  const { formId } = useParams<{ formId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!formId) {
    return <ErrorPage404 />;
  }

  return <EditForm id={formId} />;
}

function ClientCertificatesPageWrapper() {
  const [accessDenied, setAccessDenied] = useState(false);
  if (accessDenied) {
    return <ErrorPage403 />;
  }
  return (
    <CertificateList clientMode onAccessDenied={() => setAccessDenied(true)} />
  );
}

function CertificateListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (roleLoading) {
    return null;
  }

  if (role === 'CLIENT') {
    return <ClientCertificatesPageWrapper />;
  }

  if (role !== null && !['ADMIN', 'SUPER_ADMIN'].includes(role)) {
    return <ErrorPage403 />;
  }

  return <CertificateList />;
}

function ViewCertificateTemplateWrapper() {
  const { templateId } = useParams<{ templateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!templateId) {
    return <ErrorPage404 />;
  }

  return <ViewCertificateTemplate templateId={templateId} />;
}

function CreateCertificateTemplateWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <CreateCertificateTemplate />;
}

function QmsCategoryListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <QmsCategoryList />;
}

function CreateQmsCategoryWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <CreateQmsCategory />;
}

function ViewQmsCategoryWrapper() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!categoryId) {
    return <ErrorPage404 />;
  }

  return <ViewQmsCategory categoryId={categoryId} />;
}

function EditQmsCategoryWrapper() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!categoryId) {
    return <ErrorPage404 />;
  }

  return <EditQmsCategory categoryId={categoryId} />;
}

function EditCertificateTemplateWrapper() {
  const { templateId } = useParams<{ templateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  if (!templateId) {
    return <ErrorPage404 />;
  }

  return <EditCertificateTemplate templateId={templateId} />;
}

function TechnicianSelfSchedulePageWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <TechnicianSelfSchedulePage />;
}

function SubmittedFormListWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (!privileges.canListSubmittedForms) {
    return <ErrorPage403 />;
  }

  return <SubmittedFormList />;
}

function ViewSubmittedFormWrapper() {
  const { submittedFormId } = useParams<{ submittedFormId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!submittedFormId) {
    return <ErrorPage404 />;
  }

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (!privileges.canViewSubmittedForms) {
    return <ErrorPage403 />;
  }

  return <ViewSubmittedForm submittedFormId={submittedFormId} />;
}

function MyCertificatesWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();

  if (!roleLoading && role !== null && role !== 'TECHNICIAN') {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <MyCertificates />
    </Suspense>
  );
}

function ViewCertificateWrapper() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!certificateId) {
    return <ErrorPage404 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  // Accessible to TECHNICIAN (with canViewCertificate), CLIENT (with canListCertificates), ADMIN, SUPER_ADMIN
  if (
    role !== null &&
    !['TECHNICIAN', 'CLIENT', 'ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (role === 'TECHNICIAN' && !privileges.canViewCertificate) {
    return <ErrorPage403 />;
  }

  if (role === 'CLIENT' && !privileges.canListCertificates) {
    return <ErrorPage403 />;
  }

  return (
    <Suspense fallback={null}>
      <ViewCertificate certificateId={certificateId} />
    </Suspense>
  );
}

/**
 * Admin/super-admin certificate detail page under the `/Certyfikaty` section
 * (breadcrumb: Certyfikaty > Podgląd certyfikatu). Reuses the shared
 * ViewCertificate component but with the admin filename convention
 * (`CertificateID-dd-mm-yyyy.pdf`). Gated on the `certificateView` privilege.
 */
function AdminViewCertificateWrapper() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!certificateId) {
    return <ErrorPage404 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  if (role !== null && !['ADMIN', 'SUPER_ADMIN'].includes(role)) {
    return <ErrorPage403 />;
  }

  if (!privileges.certificateView) {
    return <ErrorPage403 />;
  }

  return (
    <Suspense fallback={null}>
      <ViewCertificate certificateId={certificateId} filenameFormat="date" />
    </Suspense>
  );
}

function CreateContactRequestWrapper() {
  const { role, isLoading: roleLoading } = useUserRole();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  if (!roleLoading && role !== null && role !== 'CLIENT') {
    return <ErrorPage403 />;
  }

  if (
    !privilegesLoading &&
    role === 'CLIENT' &&
    !privileges.canCreateContactRequest
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading || privilegesLoading) {
    return null;
  }

  return <CreateContactRequest />;
}

function EditAdminPageWrapper() {
  const { userId } = useParams<{ userId: string }>();
  const { role, isLoading: roleLoading } = useUserRole();

  if (!userId) {
    return <ErrorPage404 />;
  }

  if (
    !roleLoading &&
    role !== null &&
    !['ADMIN', 'SUPER_ADMIN'].includes(role)
  ) {
    return <ErrorPage403 />;
  }

  if (roleLoading) {
    return null;
  }

  return <EditAdmin />;
}

function ViewSubscriptionPlanPageWrapper() {
  const { planId } = useParams<{ planId: string }>();
  if (!planId) {
    return <ErrorPage404 />;
  }
  return <ViewSubscriptionPlanPage planId={planId} />;
}

function ViewServiceTypePageWrapper() {
  const { serviceTypeId } = useParams<{ serviceTypeId: string }>();
  if (!serviceTypeId) {
    return <ErrorPage404 />;
  }
  return <ViewServiceType serviceTypeId={serviceTypeId} />;
}

function EditSubscriptionPageWrapper() {
  return <EditSubscription />;
}

function OffersPage() {
  const location = useLocation();
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      setShowSuccessSnackbar(true);
      window.history.replaceState(null, '');
    }
  }, [location.state]);

  return (
    <>
      <OfferList />
      <Snackbar
        open={showSuccessSnackbar}
        message={successMessage}
        variant="success"
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
}

// Every logged-in page, downloaded in the background once the user starts
// using the app (see ProtectedPageLayout) so opening a page never waits for its
// code
const LOGGED_IN_PAGES = [
  ErrorPage404,
  ErrorPage403,
  ProfilePage,
  EditProfile,
  AdminList,
  AddUser,
  ViewAdminPage,
  EditAdmin,
  TechnicianList,
  AddTechnician,
  EditTechnician,
  ViewTechnicianPage,
  ClientList,
  AddClient,
  EditClient,
  ViewClientPage,
  PermissionsPage,
  SubscriptionList,
  CreateSubscription,
  ViewSubscriptionPlanPage,
  EditSubscription,
  OfferList,
  CreateOffer,
  ViewOffer,
  EditOffer,
  FacilityListPage,
  AddFacilityPage,
  ViewFacilityPage,
  EditFacilityPage,
  ServiceTypeList,
  CreateServiceType,
  ViewServiceType,
  EditServiceType,
  AvailableServiceList,
  CreateServiceRequest,
  ViewServiceRequest,
  ContactRequestList,
  CreateContactRequest,
  ViewContactRequest,
  FormList,
  CreateForm,
  ViewForm,
  EditForm,
  FillTaskForm,
  SubmittedFormList,
  ViewSubmittedForm,
  CertificateList,
  ViewCertificateTemplate,
  CreateCertificateTemplate,
  EditCertificateTemplate,
  QmsCategoryList,
  CreateQmsCategory,
  ViewQmsCategory,
  EditQmsCategory,
  TechnicianSchedulePage,
  TechnicianSelfSchedulePage,
  MyCertificates,
  ViewCertificate,
  MyBilling,
  TemperoryFile,
];

function preloadLoggedInPages() {
  for (const page of LOGGED_IN_PAGES) {
    // A failed preload is harmless: the page loads again when opened
    page.preload().catch(() => {});
  }
}

/**
 * Shared layout wrapper for all protected pages.
 * Uses <Outlet /> so child routes render inside the app shell.
 */
function ProtectedPageLayout() {
  const location = useLocation();

  // Download every other page in the background once the user starts using
  // this one (see runAfterFirstInteraction for why it waits)
  useEffect(() => runAfterFirstInteraction(preloadLoggedInPages), []);

  return (
    <MinimalLayout>
      <ProtectedRoute redirectTo="/login">
        <LayOut>
          <RouteFlashSnackbar stateKey="navigationSuccessMessage" />
          {/* Pages are code-split. Until a page's chunk is in (only on a cold
              load — all pages are preloaded in the background), keep the
              content area empty: each page shows its own loader for its data,
              and a second loader here made two different loaders flash */}
          {/* A page whose code fails to download shows a retry message here
              (sidebar stays) instead of blanking the whole app */}
          <ChunkErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </ChunkErrorBoundary>
        </LayOut>
      </ProtectedRoute>
    </MinimalLayout>
  );
}

export default function NavigationRoutes() {
  const location = useLocation();

  // Use planId as key for edit subscription routes to prevent remounting on preview navigation
  // For other routes, use pathname as key
  const getRoutesKey = () => {
    const pathname = location.pathname;
    // Check if it's an edit subscription route (with or without preview)
    const editSubscriptionMatch = pathname.match(
      /^\/Plany\/Edycja-planu\/([^/]+)/
    );
    if (editSubscriptionMatch) {
      // Use planId as key so preview navigation doesn't remount component
      return `edit-subscription-${editSubscriptionMatch[1]}`;
    }
    // Stable key for add-client stepper — prevents remount on step navigation
    // so form state (including uploaded files) is preserved across krok-1 ↔ krok-2
    if (pathname.startsWith('/Klienci/Dodaj-klienta')) {
      return 'add-client';
    }
    // Stable key for edit-client stepper — same reason, keyed by clientId so
    // editing two different clients in the same session stays independent.
    const editClientMatch = pathname.match(
      /^\/Klienci\/Edycja-klienta\/([^/]+)/
    );
    if (editClientMatch) {
      return `edit-client-${editClientMatch[1]}`;
    }
    return 'app';
  };

  return (
    <ChunkErrorBoundary resetKey={location.pathname}>
      <Suspense
        fallback={
          <div className="flex items-center justify-center bg-white h-screen w-screen">
            <Loader />
          </div>
        }
      >
        <Routes location={location} key={getRoutesKey()}>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <MinimalLayout>
                <PublicRoute redirectTo="/">
                  <LoginPage />
                </PublicRoute>
              </MinimalLayout>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <MinimalLayout>
                <Suspense fallback={null}>
                  <ForgotPassword />
                </Suspense>
              </MinimalLayout>
            }
          />
          <Route
            path="/reset-password"
            element={
              <MinimalLayout>
                <Suspense fallback={null}>
                  <ResetPassword />
                </Suspense>
              </MinimalLayout>
            }
          />
          <Route
            path="/reset-password/link-expired"
            element={
              <MinimalLayout>
                <Suspense fallback={null}>
                  <LinkExpired />
                </Suspense>
              </MinimalLayout>
            }
          />
          <Route
            path="/new-password-required"
            element={
              <MinimalLayout>
                <NewPasswordRequiredRoute>
                  <Suspense fallback={null}>
                    <NewPasswordRequired />
                  </Suspense>
                </NewPasswordRequiredRoute>
              </MinimalLayout>
            }
          />

          {/* Protected routes — single shared app shell; all children share one mounted layout */}
          <Route element={<ProtectedPageLayout />}>
            <Route path="/" element={<Navigate to="/Panel-główny" replace />} />
            <Route path="/Profil" element={<ProfilePage />} />
            <Route path="/Profil/Edytuj-profil" element={<EditProfile />} />
            <Route path="/Panel-główny" element={<TemperoryFile />} />

            {/* Administrators */}
            <Route path="/administrators">
              <Route index element={<AdminList />} />
              <Route path="addUser" element={<AddUser />} />
              <Route
                path="Edycja-Administratora/:userId"
                element={<EditAdminPageWrapper />}
              />
              <Route
                path="Podgląd-Administratora/:userId"
                element={<ViewAdminPageWrapper />}
              />
            </Route>

            {/* Technicians */}
            <Route path="/technicians">
              <Route index element={<TechnicianList />} />
              <Route path="addTechnician" element={<AddTechnician />} />
              <Route
                path="Edycja-technika/:technicianId"
                element={<EditTechnicianWrapper />}
              />
              <Route
                path="Podgląd-technika/:technicianId"
                element={<ViewTechnicianPageWrapper />}
              />
              <Route
                path="Zobacz-kalendarz"
                element={
                  <Suspense fallback={null}>
                    <TechnicianSchedulePage />
                  </Suspense>
                }
              />
            </Route>

            {/* Subscription plans */}
            <Route path="/Plany">
              <Route index element={<SubscriptionList />} />
              <Route path="Utwórz-plan" element={<CreateSubscription />} />
              <Route
                path="Podgląd-planu/:planId"
                element={<ViewSubscriptionPlanPageWrapper />}
              />
              <Route
                path="Edycja-planu/:planId"
                element={<EditSubscriptionPageWrapper />}
              />
              <Route
                path="Edycja-planu/:planId/Podgląd-planu"
                element={<EditSubscriptionPageWrapper />}
              />
            </Route>

            {/* Offers */}
            <Route path="/Oferty">
              <Route index element={<OffersPage />} />
              <Route path="Utwórz-ofertę" element={<CreateOffer />} />
              <Route path="Podgląd-oferty/:offerId" element={<ViewOffer />} />
              <Route path="Edytuj-ofertę/:offerId" element={<EditOffer />} />
            </Route>

            {/* Clients */}
            <Route path="/Klienci">
              <Route index element={<ClientList />} />
              <Route
                path="Podgląd-klienta/:clientId"
                element={<ViewClientPageWrapper />}
              />
              <Route path="Uprawnienia" element={<PermissionsPage />} />
              <Route
                path="Dodaj-klienta"
                element={
                  <Navigate to="/Klienci/Dodaj-klienta/krok-1" replace />
                }
              />
              <Route path="Dodaj-klienta/krok-1" element={<AddClient />} />
              <Route
                path="Dodaj-klienta/:clientId/krok-1"
                element={<AddClient />}
              />
              <Route
                path="Dodaj-klienta/:clientId/krok-2"
                element={<AddClient />}
              />
              <Route
                path="Dodaj-klienta/:clientId/krok-3"
                element={<AddClient />}
              />
              <Route
                path="Dodaj-klienta/:clientId/krok-4"
                element={<AddClient />}
              />
              <Route
                path="Edycja-klienta/:clientId"
                element={<EditClientRedirect />}
              />
              <Route
                path="Edycja-klienta/:clientId/krok-1"
                element={<EditClient />}
              />
              <Route
                path="Edycja-klienta/:clientId/krok-2"
                element={<EditClient />}
              />
              <Route
                path="Edycja-klienta/:clientId/krok-3"
                element={<EditClient />}
              />
              <Route
                path="Edycja-klienta/:clientId/krok-4"
                element={<EditClient />}
              />
            </Route>

            {/* Products & Services (client only) */}
            <Route path="/Produkty i usługi">
              <Route index element={<AvailableServiceListWrapper />} />
              <Route
                path=":serviceTypeName"
                element={<CreateServiceRequestWrapper />}
              />
              <Route
                path="Podgląd-zgłoszenia/:serviceRequestId"
                element={<ViewServiceRequestWrapper />}
              />
            </Route>

            {/* Billing (client only) */}
            <Route path="/Moje-rozliczenia" element={<MyBillingWrapper />} />

            {/* Submitted forms (TECHNICIAN role only) */}
            <Route
              path="/Przesłane-formularze"
              element={<SubmittedFormListWrapper />}
            />
            <Route
              path="/Przesłane-formularze/Podgląd/:submittedFormId"
              element={<ViewSubmittedFormWrapper />}
            />

            {/* Technician personal calendar (TECHNICIAN role only) */}
            <Route
              path="/Moje-zadania"
              element={
                <Suspense fallback={null}>
                  <TechnicianSelfSchedulePageWrapper />
                </Suspense>
              }
            >
              <Route
                path="Podgląd-zgłoszenia/:serviceRequestId"
                element={<ViewServiceRequestWrapper />}
              />
              <Route
                path="Podgląd-zgłoszenia/:serviceRequestId/formularz-zadania"
                element={<FillTaskFormWrapper />}
              />
            </Route>

            {/* Technician certificates */}
            <Route path="/Moje-certyfikaty">
              <Route index element={<MyCertificatesWrapper />} />
              <Route
                path=":certificateId"
                element={<ViewCertificateWrapper />}
              />
            </Route>

            {/* Contact requests */}
            <Route path="/Zgłoszenia">
              <Route index element={<ContactRequestListWrapper />} />
              <Route
                path="Podgląd-zgłoszenia/:serviceRequestId"
                element={<ViewServiceRequestWrapper />}
              />
              <Route
                path="Podgląd-zgłoszenia/:serviceRequestId/formularz-zadania"
                element={<FillTaskFormWrapper />}
              />
              <Route
                path="Podgląd-mojego-zadania/:serviceRequestId"
                element={<MyAssignedTaskViewWrapper />}
              />
              <Route
                path="Podgląd-zgłoszenia-kontaktowego/:contactRequestId"
                element={<ViewContactRequestWrapper />}
              />
              <Route
                path="Podgląd-mojego-zadania-kontaktowego/:contactRequestId"
                element={<ViewMyAssignedContactRequestWrapper />}
              />
            </Route>
            <Route
              path="/Zgłoszenia-kontaktowe/Utwórz-zgłoszenie-kontaktowe"
              element={<CreateContactRequestWrapper />}
            />

            {/* Service types (admin / super admin) */}
            <Route path="/Typy-usług">
              <Route index element={<ServiceTypeList />} />
              <Route path="Dodaj-typ-usługi" element={<CreateServiceType />} />
              <Route
                path="Podgląd-typu-usługi/:serviceTypeId"
                element={<ViewServiceTypePageWrapper />}
              />
              <Route
                path="Edycja-typu-usługi/:serviceTypeId"
                element={<EditServiceType />}
              />
            </Route>

            {/* Facilities */}
            <Route path="/Placówki">
              <Route index element={<FacilityListPage />} />
              <Route
                path="Dodaj-placówkę"
                element={<AddFacilityPageWrapper />}
              />
              <Route
                path=":facilityId/Podgląd-placówki"
                element={<ViewFacilityPageWrapper />}
              />
              <Route
                path=":facilityId/Edycja-placówki"
                element={<EditFacilityPageWrapper />}
              />
            </Route>

            {/* Task forms */}
            <Route path="/Formularze-zadań">
              <Route index element={<FormListWrapper />} />
              <Route path="Utwórz-formularz" element={<CreateFormWrapper />} />
              <Route
                path="Podgląd-Formularza/:formId"
                element={<ViewFormWrapper />}
              />
              <Route
                path="Edytuj-Formularz/:formId"
                element={<EditFormWrapper />}
              />
            </Route>

            {/* Certificates */}
            <Route path="/Certyfikaty">
              <Route index element={<CertificateListWrapper />} />
              <Route
                path="Podgląd-certyfikatu/:certificateId"
                element={<AdminViewCertificateWrapper />}
              />
              <Route
                path="Podgląd-szablonu/:templateId"
                element={<ViewCertificateTemplateWrapper />}
              />
              <Route
                path="Utwórz-szablon-certyfikatu"
                element={<CreateCertificateTemplateWrapper />}
              />
              <Route
                path="Edycja-szablonu/:templateId"
                element={<EditCertificateTemplateWrapper />}
              />
            </Route>

            {/* QMS — Document Categories (admin / super admin) */}
            <Route path="/Kategorie">
              <Route index element={<QmsCategoryListWrapper />} />
              <Route
                path="Dodaj-kategorię"
                element={<CreateQmsCategoryWrapper />}
              />
              <Route
                path="Podgląd/:categoryId"
                element={<ViewQmsCategoryWrapper />}
              />
              <Route
                path="Edycja/:categoryId"
                element={<EditQmsCategoryWrapper />}
              />
            </Route>
          </Route>

          <Route
            path="/403"
            element={
              <div className="flex min-h-screen w-full flex-col">
                <ErrorPage403 />
              </div>
            }
          />
          <Route path="*" element={<ErrorPage404 />} />
        </Routes>
      </Suspense>
    </ChunkErrorBoundary>
  );
}
